/**
 * Lupuloa Pack Builder - Modern Mix & Match
 * Constructor de packs personalizados para Shopify
 */

class PackBuilder {
  constructor() {
    this.packSize = parseInt(document.querySelector('.pack-builder-container')?.dataset.packSize) || 12;
    this.selectedProducts = new Map(); // productId => {title, quantity, image, price}
    this.productsGrid = document.getElementById('products-grid');
    this.addPackButton = document.getElementById('add-pack-to-cart');
    this.clearButton = document.getElementById('clear-selection');
    this.totalBeersEl = document.getElementById('total-beers');
    this.totalPriceEl = document.getElementById('total-price');
    this.messageEl = document.getElementById('pack-message');
    this.selectedBeersGrid = document.getElementById('selected-beers-grid');
    
    this.init();
  }

  init() {
    if (!this.productsGrid) return;
    
    this.attachEventListeners();
    this.updateUI();
  }

  attachEventListeners() {
    // Event listener para botones +/- en las tarjetas
    this.productsGrid.addEventListener('click', (e) => {
      const addButton = e.target.closest('.btn-add-corner');
      const removeButton = e.target.closest('.btn-remove-corner');
      
      if (addButton) {
        const productCard = addButton.closest('.product-card');
        if (productCard) {
          this.addProduct(productCard);
        }
      } else if (removeButton) {
        const productCard = removeButton.closest('.product-card');
        if (productCard) {
          this.removeProduct(productCard);
        }
      }
    });

    // Event listener para limpiar selección
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clearSelection());
    }

    // Event listener para agregar al carrito
    if (this.addPackButton) {
      this.addPackButton.addEventListener('click', () => this.addPackToCart());
    }
  }

  addProduct(productCard) {
    const productId = productCard.dataset.productId;
    const totalSelected = this.getTotalSelected();

    if (totalSelected >= this.packSize) {
      this.showMessage(`Ya has seleccionado ${this.packSize} cervezas.`, 'warning');
      return;
    }

    // Obtener o inicializar el producto
    let product = this.selectedProducts.get(productId);
    if (!product) {
      product = {
        title: productCard.dataset.productTitle,
        quantity: 0,
        image: productCard.dataset.productImage,
        price: parseFloat(productCard.dataset.productPrice) || 0
      };
    }

    product.quantity++;
    this.selectedProducts.set(productId, product);
    
    this.updateProductCard(productCard, product.quantity);
    this.updateUI();
  }

  removeProduct(productCard) {
    const productId = productCard.dataset.productId;
    let product = this.selectedProducts.get(productId);
    
    if (!product || product.quantity === 0) return;

    product.quantity--;
    
    if (product.quantity === 0) {
      this.selectedProducts.delete(productId);
    } else {
      this.selectedProducts.set(productId, product);
    }
    
    this.updateProductCard(productCard, product.quantity);
    this.updateUI();
  }

  clearSelection() {
    this.selectedProducts.clear();
    
    const productCards = this.productsGrid.querySelectorAll('.product-card');
    productCards.forEach(card => {
      card.classList.remove('selected');
      const badge = card.querySelector('.product-quantity-badge');
      badge.style.display = 'none';
      badge.textContent = '0';
    });
    
    this.updateUI();
  }

  updateProductCard(productCard, quantity) {
    const badge = productCard.querySelector('.product-quantity-badge');
    
    if (quantity > 0) {
      productCard.classList.add('selected');
      badge.textContent = quantity;
      badge.style.display = 'flex';
    } else {
      productCard.classList.remove('selected');
      badge.style.display = 'none';
    }
  }

  getTotalSelected() {
    let total = 0;
    this.selectedProducts.forEach(product => {
      total += product.quantity;
    });
    return total;
  }

  getTotalPrice() {
    let total = 0;
    this.selectedProducts.forEach(product => {
      total += product.price * product.quantity;
    });
    return total;
  }

  updateUI() {
    const totalSelected = this.getTotalSelected();
    const totalPrice = this.getTotalPrice();
    
    if (this.totalBeersEl) {
      this.totalBeersEl.textContent = totalSelected;
    }

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = totalPrice.toFixed(2) + ' €';
    }

    // Actualizar grid circular de cervezas seleccionadas
    this.updateSelectedBeersGrid();
    
    if (this.addPackButton) {
      if (totalSelected === this.packSize) {
        this.addPackButton.disabled = false;
        this.addPackButton.classList.add('enabled');
        this.showMessage(`¡Perfecto! Tu pack está completo.`, 'success');
      } else {
        this.addPackButton.disabled = true;
        this.addPackButton.classList.remove('enabled');
        
        if (totalSelected > 0) {
          const remaining = this.packSize - totalSelected;
          this.showMessage(`Te faltan ${remaining} cerveza${remaining !== 1 ? 's' : ''} para completar tu pack.`, 'info');
        } else {
          this.showMessage('', '');
        }
      }
    }
  }

  updateSelectedBeersGrid() {
    if (!this.selectedBeersGrid) return;

    const slots = this.selectedBeersGrid.querySelectorAll('.beer-slot');
    let currentIndex = 0;

    // Limpiar todos los slots
    slots.forEach(slot => {
      slot.classList.remove('filled');
      slot.innerHTML = `<span>${slot.dataset.slot}</span>`;
    });

    // Llenar los slots con las cervezas seleccionadas
    this.selectedProducts.forEach((product, productId) => {
      for (let i = 0; i < product.quantity; i++) {
        if (currentIndex < this.packSize && currentIndex < slots.length) {
          const slot = slots[currentIndex];
          slot.classList.add('filled');
          
          if (product.image) {
            slot.innerHTML = `<img src="${product.image}" alt="${product.title}" class="beer-slot-image">`;
          } else {
            slot.innerHTML = `<span>${product.title.charAt(0)}</span>`;
          }
          
          currentIndex++;
        }
      }
    });
  }

  showMessage(message, type = 'info') {
    if (!this.messageEl) return;
    
    this.messageEl.textContent = message;
    this.messageEl.className = 'pack-message';
    
    if (type) {
      this.messageEl.classList.add(`message-${type}`);
    }
  }

  async addPackToCart() {
    const totalSelected = this.getTotalSelected();
    
    if (totalSelected !== this.packSize) {
      this.showMessage(`Debes seleccionar exactamente ${this.packSize} cervezas.`, 'error');
      return;
    }

    this.addPackButton.disabled = true;
    this.addPackButton.textContent = 'Añadiendo...';
    this.showMessage('Añadiendo cervezas al carrito...', 'info');

    try {
      // Generar ID único para este pack
      const packId = `pack_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Preparar array de items para añadir al carrito
      const items = [];
      this.selectedProducts.forEach((product, productId) => {
        if (product.quantity > 0) {
          items.push({
            id: productId,
            quantity: product.quantity,
            properties: {
              '_pack_id': packId,
              '_pack_size': this.packSize
            }
          });
        }
      });

      // Añadir todos los productos al carrito usando la API de Shopify
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items
        })
      });

      if (!response.ok) {
        throw new Error('Error al añadir al carrito');
      }

      const result = await response.json();
      
      this.showMessage('¡Cervezas añadidas al carrito con éxito!', 'success');
      
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1000);

    } catch (error) {
      console.error('Error:', error);
      this.showMessage('Hubo un error al añadir las cervezas. Por favor, inténtalo de nuevo.', 'error');
      this.addPackButton.disabled = false;
      this.addPackButton.textContent = 'Añadir Pack al Carrito';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new PackBuilder();
});